import apiClient from './client.js';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys.js';
import { PLATFORMS } from '../constants/platforms.js';
import { normalizeHandle } from '../utils.js';
import { commentPatchBody } from '../lib/leadNote.mjs';

/**
 * Fetch paginated leads by status (for kanban columns).
 * GET /api/leads?status={statusId}&page={page}&limit={limit}&...
 */
export async function fetchLeadsByStatus ({
  statusId,
  page = 1,
  limit = 30,
  search,
  managerFilter,
  statusIn,
  lastContactDays,
  sortBy = 'updatedAt',
  sortOrder = 'desc'
}) {
  const params = { page, limit, sortBy, sortOrder };
  if (statusIn) {
    params.statusIn = statusIn;
  } else {
    params.status = statusId;
  }
  if (search) params.search = search;
  if (managerFilter && managerFilter !== 'all') params.scout = managerFilter;
  if (lastContactDays && lastContactDays !== 'all') params.lastContactDays = lastContactDays;

  const { data } = await apiClient.get('/leads', { params });
  return {
    leads: data?.leads || [],
    pagination: data?.pagination || { page, limit, total: 0, totalPages: 0 }
  };
}

/**
 * Fetch leads in the statuses of `statusFilter`, by default the test and ambassador statuses.
 * GET /api/leads?statusIn=testing,round_2,...
 */
export async function fetchTestingLeads ({
  page = 1,
  limit = 30,
  search,
  managerFilter,
  statusFilter,
  sortBy = 'updatedAt',
  sortOrder = 'desc'
}) {
  const defaultStatuses = 'testing,round_2,round_3,round_4,active_amb,on_quota';
  const params = {
    statusIn: statusFilter || defaultStatuses,
    page,
    limit,
    sortBy,
    sortOrder
  };
  if (search) params.search = search;
  if (managerFilter && managerFilter !== 'all') params.scout = managerFilter;

  const { data } = await apiClient.get('/leads', { params });
  return {
    leads: data?.leads || [],
    scouts: data?.scouts || [],
    pagination: data?.pagination || { page, limit, total: 0, totalPages: 0 }
  };
}

/**
 * Fetch a single lead by ID.
 * GET /api/leads/{leadId}
 */
export async function fetchLead (leadId) {
  const { data } = await apiClient.get(`/leads/${leadId}`);
  return data?.lead || null;
}

/**
 * Update lead fields.
 * PATCH /api/leads/{leadId}
 */
export async function updateLead (leadId, payload) {
  const { data } = await apiClient.patch(`/leads/${leadId}`, payload);
  return data?.lead;
}

/**
 * Save the card note alone: the body is `{ comment }` and nothing else.
 * PATCH /api/leads/{leadId}
 */
export async function updateLeadComment (leadId, comment) {
  const { data } = await apiClient.patch(`/leads/${leadId}`, commentPatchBody(comment));
  return data?.lead;
}

/**
 * Pin (or unpin) a lead as the original of its blogger group.
 * @param {string} leadId
 * @param {boolean} [pin=true]
 * @returns {Promise<{ ok:boolean, pinned:boolean }>}
 */
export async function makeLeadOriginal (leadId, pin = true) {
  const { data } = await apiClient.post(`/leads/${leadId}/make-original`, { pin });
  return data;
}

/**
 * Create a new lead.
 * POST /api/leads
 */
export async function createLead (payload) {
  const { data } = await apiClient.post('/leads', payload);
  return data?.lead;
}

export async function importLeadsCsv ({ rows, campaignId, claimLeadIds, application, tagLabel } = {}) {
  const body = {};
  if (rows) body.rows = rows;
  if (campaignId) body.campaignId = campaignId;
  if (claimLeadIds) body.claimLeadIds = claimLeadIds;
  if (application) body.application = application;
  if (tagLabel) body.tagLabel = tagLabel;
  const { data } = await apiClient.post('/leads/import-csv', body, { timeout: 120000 });
  return data;
}

/**
 * Delete a lead.
 * DELETE /api/leads/{leadId}
 */
export async function deleteLead (leadId) {
  await apiClient.delete(`/leads/${leadId}`);
}

/**
 * Bulk delete leads from CRM; leads that could not be deleted come back in `failed[]`.
 * POST /api/leads/bulk-delete
 * @param {{ leadIds: string[] } | { selectAllFiltered: true, filters: Object }} payload
 */
export async function bulkDeleteLeads (payload) {
  const { data } = await apiClient.post('/leads/bulk-delete', payload);
  return data;
}

/**
 * Bulk-assign one tag to a selection or to the full filtered set.
 * POST /api/leads/bulk-tag
 * @param {{ tagLabel: string, leadIds: string[] } | { tagLabel: string, selectAllFiltered: true, filters: Object }} payload
 */
export async function bulkTagLeads (payload) {
  const { data } = await apiClient.post('/leads/bulk-tag', payload);
  return data;
}

/**
 * Fetch kanban board stats.
 * GET /api/leads/board-stats
 */
export async function fetchBoardStats () {
  const { data } = await apiClient.get('/leads/board-stats');
  return data?.stats || { total: 0, active: 0, completed: 0, inbox: 0 };
}

/**
 * Resync a lead from Airtable.
 * POST /api/leads/{leadId}/resync
 */
export async function resyncLead (leadId) {
  const { data } = await apiClient.post(`/leads/${leadId}/resync`);
  return data;
}

/**
 * Check for duplicate leads by identifier fields.
 * POST /api/leads/check-duplicates
 */
export async function checkDuplicates ({ instagram, tiktok, email, excludeId }) {
  const { data } = await apiClient.post('/leads/check-duplicates', { instagram, tiktok, email, excludeId });
  return data;
}

export async function previewImport (rows) {
  const { data } = await apiClient.post('/leads/preview-import', { rows });
  return data;
}

/**
 * Fetch all TrackedAccount rows (primary + extras) for a lead.
 * GET /api/leads/:leadId/accounts → `{ ok: true, accounts: [...] }`;
 * a refusal answers `{ ok: false, code, error }` and the call rejects.
 */
export async function fetchLeadAccounts (leadId) {
  const { data } = await apiClient.get(`/leads/${leadId}/accounts`);
  return data;
}

/**
 * TanStack Query hook — single client-side source for «what accounts does
 * this lead have».
 */
export function useLeadAccounts (leadId) {
  return useQuery({
    queryKey: leadId ? queryKeys.accounts.detail(leadId) : ['accounts', 'disabled'],
    queryFn: () => fetchLeadAccounts(leadId),
    enabled: !!leadId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** GET /api/leads/:id/airtable-links — активные Air-записи лида. */
export function useLeadAirtableLinks (leadId) {
  return useQuery({
    queryKey: ['leads', 'airtableLinks', leadId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/leads/${leadId}/airtable-links`);
      return data?.links ?? [];
    },
    enabled: !!leadId,
    staleTime: 300_000,
  });
}

/**
 * Create a lead in Airtable (single).
 * POST /api/leads/{leadId}/airtable
 */
export async function createInAirtable (leadId) {
  const { data } = await apiClient.post(`/leads/${leadId}/airtable`);
  return data;
}

/**
 * Bulk-create leads in Airtable.
 * POST /api/leads/airtable/bulk-create
 * @param {string[] | { selectAllFiltered: true, filters: Object }} payload
 *   - Array of leadIds for selection mode
 *   - { selectAllFiltered: true, filters } for filter mode (full filtered set, async)
 */
export async function bulkCreateInAirtable (payload) {
  const body = Array.isArray(payload) ? { leadIds: payload } : payload;
  const { data } = await apiClient.post('/leads/airtable/bulk-create', body);
  return data;
}

/**
 * Bulk mark leads as duplicates in Airtable.
 * POST /api/leads/airtable/bulk-mark-dup
 * @param {string[] | { selectAllFiltered: true, filters: Object }} payload
 *   - Array of leadIds for selection mode
 *   - { selectAllFiltered: true, filters } for filter mode (full filtered set, async)
 */
export async function bulkMarkDupInAirtable (payload) {
  const body = Array.isArray(payload) ? { leadIds: payload } : payload;
  const { data } = await apiClient.post('/leads/airtable/bulk-mark-dup', body);
  return data;
}

/**
 * Returns null for an empty value, the normalized handle when the seam parses it,
 * else the value as typed, so the route refuses it with a 400 and a reason
 * instead of reading it as a clear.
 */
export const keepRawHandle = (value, platform) => {
  const raw = value == null ? '' : String(value);
  return raw.trim() === '' ? null : (normalizeHandle(raw, platform) ?? raw);
};

export function buildLeadWritePayload (form, extraAccounts = [], payment = null) {
  const { instagram, tiktok, youtube, facebook, threads, x, ...rest } = form;

  const normalizedExtras = (extraAccounts || [])
    .map(a => (a && a.handle
      ? { ...a, handle: keepRawHandle(a.handle, a.platform) }
      : null))
    .filter(a => a && a.handle);

  const accounts = [
    instagram && { platform: PLATFORMS.INSTAGRAM, handle: normalizeHandle(instagram, PLATFORMS.INSTAGRAM), isPrimary: true },
    tiktok    && { platform: PLATFORMS.TIKTOK,    handle: normalizeHandle(tiktok,    PLATFORMS.TIKTOK),    isPrimary: true },
    youtube   && { platform: PLATFORMS.YOUTUBE,   handle: normalizeHandle(youtube,   PLATFORMS.YOUTUBE),   isPrimary: true },
    facebook  && { platform: PLATFORMS.FACEBOOK,  handle: normalizeHandle(facebook,  PLATFORMS.FACEBOOK),  isPrimary: true },
    threads   && { platform: 'threads', handle: normalizeHandle(threads, 'threads'), isPrimary: true },
    x         && { platform: 'x',       handle: normalizeHandle(x,       'x'),       isPrimary: true },
    ...normalizedExtras.map(a => ({ platform: a.platform, handle: a.handle, isPrimary: false })),
  ].filter(a => a && a.handle && String(a.handle).length > 0);

  return {
    ...rest,
    instagram: keepRawHandle(instagram, PLATFORMS.INSTAGRAM),
    tiktok:    keepRawHandle(tiktok,    PLATFORMS.TIKTOK),
    youtube:   keepRawHandle(youtube,   PLATFORMS.YOUTUBE),
    facebook:  keepRawHandle(facebook,  PLATFORMS.FACEBOOK),
    threads:   keepRawHandle(threads,   'threads'),
    x:         keepRawHandle(x,         'x'),
    accounts,
    extraAccounts: normalizedExtras,
    payment,
  };
}
